import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import profileImage from "@/assets/federico-profile.png";
import espressoFocusAsset from "@/assets/espresso-focus.jpg.asset.json";

interface NewsPost {
  id: string;
  title_es: string;
  title_en: string;
  title_it: string;
  description_es: string;
  description_en: string;
  description_it: string;
  image_url: string | null;
  created_at: string;
}

export const News = () => {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchPosts = async () => {
      const { data } = await supabase
        .from("news_posts")
        .select("*")
        .order("created_at", { ascending: false });
      if (data && data.length > 0) setPosts(data);
      setLoaded(true);
    };
    fetchPosts();
  }, []);

  const getTitle = (post: NewsPost) => {
    const key = `title_${language}` as keyof NewsPost;
    return (post[key] as string) || post.title_es;
  };

  const getDescription = (post: NewsPost) => {
    const key = `description_${language}` as keyof NewsPost;
    return (post[key] as string) || post.description_es;
  };

  // If no posts from DB, show default static content
  if (loaded && posts.length === 0) {
    return (
      <section className="py-24 bg-muted/50" id="novedades">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4 flex items-center justify-center gap-3">
              <Sparkles className="text-gold w-10 h-10" />
              {t("news", "title")}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t("news", "subtitle")}
            </p>
            <div className="w-24 h-1 bg-gold mx-auto mt-6"></div>
          </div>
          <div className="max-w-5xl mx-auto">
            <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-auto">
                  <img src={profileImage} alt="Federico Duronio" className="w-full h-full object-cover" />
                </div>
                <div className="relative h-64 md:h-auto bg-black">
                  <img
                    src={espressoFocusAsset.url}
                    alt="Curso Espresso Focus - 4 horas, máx. 2 personas, 240€ IVA incluido"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-muted/50" id="novedades">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4 flex items-center justify-center gap-3">
            <Sparkles className="text-gold w-10 h-10" />
            {t("news", "title")}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t("news", "subtitle")}
          </p>
          <div className="w-24 h-1 bg-gold mx-auto mt-6"></div>
        </div>

        <div className="max-w-5xl mx-auto space-y-8">
          {posts.map((post) => (
            <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-auto">
                  <img
                    src={profileImage}
                    alt="Federico Duronio"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative h-64 md:h-auto bg-black">
                  <img
                    src={post.image_url || espressoFocusAsset.url}
                    alt={getTitle(post)}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
